import classNames from 'classnames';
import { useContext, useMemo } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import {
  IssuerAddUpdatePortfolioModal,
  IssuerAddUpdatePortfolioModalProps,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdatePortfolioModal.tsx';
import {
  useIssuerDeletePortfolioMutation,
  useIssuerPortfoliosInfiniteListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import { goodFormatNumber } from 'ui/utils';
import { PortfolioModel } from 'api';

type PortfolioTable =
  | 'Portfolio name'
  | 'Portfolio value'
  | 'Available cash'
  | 'Total asset value'
  | 'PNL'
  | '';

const tableHeadItems: TableHeadColumn<PortfolioTable>[] = [
  { title: 'Portfolio name' },
  { title: 'Portfolio value' },
  { title: 'Available cash' },
  { title: 'Total asset value' },
  { title: 'PNL' },
  { title: '' },
];

export const PortfoliosTab = () => {
  const { openModal } = useContext(ModalProviderContext);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { registerCurrency } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataPortfolioList,
  } = useIssuerPortfoliosInfiniteListQuery({
    page: 1,
    page_size: 10,
  });

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataPortfolioList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataPortfolioList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(item => {
          const { id, name, portfolio_value, total_asset_value, pnl_value, total_cash } = item;
          const body: BodyCell[] = [
            {
              cell: <div className="text-blue-700">{name}</div>,
            },
            // { cell: portfolio_classes?.map(item => item.class_name).join(', ') },
            {
              cell: (
                <span>
                  {goodFormatNumber(portfolio_value || 0)} {registerCurrency}
                </span>
              ),
            },
            {
              cell: (
                <span>
                  {goodFormatNumber(total_cash || 0)} {registerCurrency}
                </span>
              ),
            },
            {
              cell: (
                <span>
                  {goodFormatNumber(total_asset_value || 0)} {registerCurrency}
                </span>
              ),
            },
            {
              cell: (
                // <img width={16} height={16} src={LockIcon} alt="disabled" />
                <div
                  className={classNames({
                    'text-[#16A34A]': (pnl_value || 0) > 0,
                    'text-[#DC2626]': (pnl_value || 0) < 0,
                    'text-[#24699F]': (pnl_value || 0) === 0,
                  })}
                >
                  <div>
                    {goodFormatNumber(pnl_value || 0)} {registerCurrency}
                  </div>
                </div>
              ),
            },
            { cell: <MoreButton item={item} /> },
          ];

          return {
            cells: body,
            onClick: () => {
              navigate(
                {
                  pathname: issuerDashboardPaths.dynamic.portfolio(id.toString()),
                  search: params.toString(),
                },
                {
                  replace: false,
                },
              );
            },
          };
        }),
      );
    });
    return result;
  }, [dataPortfolioList, navigate, params, registerCurrency]);

  const handleOpenAddPortfolioModal = () => {
    openModal(IssuerAddUpdatePortfolioModal);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full"></div>

        <div className="flex gap-2">
          <Button className="w-[144px] px-0" onClick={handleOpenAddPortfolioModal}>
            Add new portfolio
          </Button>
        </div>
      </div>
      <Table
        className="!m-0"
        classNameHead="[&>*]:text-xs"
        withBorder={false}
        withRounded={false}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface MoreButtonProps {
  item: PortfolioModel;
}

export const MoreButton = ({ item }: MoreButtonProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioMutation();
  const items = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal<IssuerAddUpdatePortfolioModalProps>(IssuerAddUpdatePortfolioModal, { item });
        },
      },
      {
        label: 'Delete',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Delete</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal]);

  return (
    <div>
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
